<template>
  <!-- Selector de idioma de la interfaz. Presente en la barra superior de cada
       pantalla previa a la sala (inicio, crear, entrar) y siempre en el mismo
       sitio: quien entra por un enlace suelto, sin el `?lang=` que añade Moodle,
       tiene que poder cambiarlo desde donde esté y no solo al final del camino.
       Dentro de la sala vive en «Mis ajustes» (SettingsTab), junto al resto de
       lo que cada uno decide para SU pantalla.

       La elección se recuerda en este navegador y no cambia la de nadie más. -->
  <div ref="root" class="lang">
    <button
      type="button"
      class="lang__trigger"
      :class="{ 'lang__trigger--open': isOpen }"
      :title="$t('settings.language')"
      :aria-label="$t('settings.language')"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <GlobeAltIcon class="lang__globe" />
      <!-- El nombre completo cuando cabe; el código cuando la barra se estrecha,
           que sigue diciendo lo mismo en tres caracteres. -->
      <span class="lang__name">{{ current.label }}</span>
      <span class="lang__code">{{ current.code.toUpperCase() }}</span>
      <ChevronDownIcon class="lang__chevron" :class="{ 'lang__chevron--open': isOpen }" />
    </button>

    <ul v-if="isOpen" class="lang__menu" role="listbox" :aria-label="$t('settings.language')">
      <li
        v-for="option in SUPPORTED_LOCALES"
        :key="option.code"
        role="option"
        :aria-selected="uiLocale === option.code"
      >
        <button
          type="button"
          class="lang__item"
          :class="{ 'lang__item--active': uiLocale === option.code }"
          @click="pick(option.code)"
        >
          <span class="lang__badge">{{ option.code.toUpperCase() }}</span>
          <span class="lang__item-name">{{ option.label }}</span>
          <CheckIcon v-if="uiLocale === option.code" class="lang__check" />
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { CheckIcon, ChevronDownIcon, GlobeAltIcon } from '@heroicons/vue/24/solid'
import { setLocale, SUPPORTED_LOCALES, type LocaleCode } from '@/i18n'

const { locale } = useI18n()

const root = ref<HTMLElement | null>(null)
const isOpen = ref(false)

/** El idioma que se está leyendo; es el que sale marcado. */
const uiLocale = computed(() => locale.value as LocaleCode)

/** Su entrada en la lista, para rotular el botón con el nombre y no el código. */
const current = computed(
  () => SUPPORTED_LOCALES.find((l) => l.code === uiLocale.value) ?? SUPPORTED_LOCALES[0]
)

const pick = (code: LocaleCode) => {
  setLocale(code)
  isOpen.value = false
}

// Se cierra como se cierra cualquier menú: pulsando fuera o con Escape. Sin
// esto, el desplegable se queda abierto encima de la pantalla que la persona ha
// venido a usar.
const onPointerDown = (event: MouseEvent) => {
  if (isOpen.value && !root.value?.contains(event.target as Node)) isOpen.value = false
}
const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') isOpen.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', onPointerDown)
  document.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onPointerDown)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
/* Mismo lenguaje que el resto de menús de la aplicación (SessionMenu, «Mis
   ajustes»): pastilla oscura, desplegable de 12 px de radio y el azul de la
   marca para lo que está seleccionado. */
.lang {
  position: relative;
  display: inline-flex;
}

.lang__trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(45, 46, 48, 0.92);
  color: #e8eaed;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.lang__trigger:hover,
.lang__trigger--open {
  background: #3c4043;
  border-color: rgba(255, 255, 255, 0.24);
}

.lang__trigger:focus-visible {
  outline: 2px solid #8ab4f8;
  outline-offset: 2px;
}

.lang__globe {
  width: 16px;
  height: 16px;
  color: #9aa0a6;
  flex-shrink: 0;
}

.lang__code {
  display: none;
  letter-spacing: 0.5px;
}

.lang__chevron {
  width: 12px;
  height: 12px;
  color: #9aa0a6;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}

.lang__chevron--open {
  transform: rotate(180deg);
}

.lang__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 60;
  min-width: 180px;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #2d2e30;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
}

.lang__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #e8eaed;
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.lang__item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.lang__item:focus-visible {
  outline: 2px solid #8ab4f8;
  outline-offset: -2px;
}

.lang__item--active {
  color: #8ab4f8;
}

/* El código hace de icono de la fila, como en «Mis ajustes»: es lo que
   distingue un idioma de otro de un vistazo, sin recurrir a banderas (que
   nombran países, no idiomas, y en Windows ni siquiera se dibujan). */
.lang__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  padding: 2px 0;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: #9aa0a6;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.lang__item--active .lang__badge {
  background: rgba(138, 180, 248, 0.16);
  color: #8ab4f8;
}

.lang__item-name {
  flex: 1;
}

.lang__check {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

/* Barras estrechas: el botón se queda en globo + código. */
@media (max-width: 600px) {
  .lang__name {
    display: none;
  }

  .lang__code {
    display: inline;
  }
}
</style>
